import React, { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import Input from "../../../components/Input/Input"
import { getBillingInfo, getBankSecrets } from "../../../features/bankAccountLink/bankAccountLink"
import { clearAlphaNumeric, clearNumber } from "../../../utils/payment"
import { LuEye, LuPencil } from "react-icons/lu"

const BankAccountContainer = ({ billingId, setBillingAddress, setBankAccountData, setBankAccountSecrets, viewOnly = false }) => {
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()
  const [reqError, setReqError] = useState('')
  const [secretsLoaded, setSecretsLoaded] = useState(false)
  const [editingSecrets, setEditingSecrets] = useState(false)
  const [bankAccount, setBankAccount] = useState({
    bankName: '',
    nameOnAccount: '',
    accountNumberLast4: '',
    billingAddress: {
      address: '',
      address2: '',
      city: '',
      state: '',
      zipcode: ''
    }
  })
  const [bankAccountSecrets, setLocalBankAccountSecrets] = useState({
    routingNumber: '',
    accountNumber: ''
  })

  useEffect(() => {
    if (reqError) {
      showSnackBar("error", reqError)
      setReqError('')
    }
  }, [reqError])

  const getBankAccountDetails = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getBillingInfo({ billingAccountId: billingId, billingType: 'bank' }))
      const { bankName, nameOnAccount, billingAddress, accountNumberLast4 } = response?.payload?.data
      setBankAccount({
        bankName: bankName || '',
        nameOnAccount: nameOnAccount || '',
        accountNumberLast4: accountNumberLast4 || '',
        billingAddress: {
          address: billingAddress?.address || '',
          address2: billingAddress?.address2 || '',
          city: billingAddress?.city || '',
          state: billingAddress?.state || '',
          zipcode: billingAddress?.zipcode || ''
        }
      })
      setBillingAddress(billingAddress)
      setBankAccountData?.({ bankName, nameOnAccount })
    } catch (error) {
      setReqError(error.message || 'Failed to get bank account data')
    }
    triggerLoading(false)
  }

  const getBankAccountSecrets = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getBankSecrets({ billingAccountId: billingId }))
      const { accountNumber, routingNumber } = response?.payload?.data
      setLocalBankAccountSecrets({
        accountNumber: accountNumber || '',
        routingNumber: routingNumber || ''
      })
      setSecretsLoaded(true)
    } catch (error) {
      setReqError(error.message || 'Failed to get bank account secrets')
    }
    triggerLoading(false)
  }

  const handleChange = ({ target }) => {
    if (viewOnly) return
    setBankAccount(prev => ({ ...prev, [target.name]: target.value }))
    setBankAccountData(prev => ({ ...prev, [target.name]: target.value }))
  }

  const handleSecretsChange = ({ target }) => {
    if (viewOnly) return

    if (target.name === 'routingNumber') {
      const maxLength = 9
      setLocalBankAccountSecrets(prev => ({ ...prev, [target.name]: clearNumber(target.value)?.slice(0, maxLength) }))
      setBankAccountSecrets(prev => ({ ...prev, [target.name]: clearNumber(target.value)?.slice(0, maxLength) }))
    }

    if (target.name === 'accountNumber') {
      const maxLength = 17
      const formatedValue = clearAlphaNumeric(target.value)?.slice(0, maxLength)
      setLocalBankAccountSecrets(prev => ({ ...prev, [target.name]: formatedValue }))
      setBankAccountSecrets(prev => ({ ...prev, [target.name]: formatedValue }))
    }
  }

  useEffect(() => {
    getBankAccountDetails()
  }, [])

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-6 pb-6 h-max border-b border-divider">
      <Input
        width="w-full"
        placeholder="Bank Name"
        label="Bank Name"
        name="bankName"
        value={bankAccount.bankName}
        onChange={handleChange}
        disabled={viewOnly}
      />
      <Input
        width="w-full"
        placeholder="Account Number"
        label="Account Number"
        name="accountNumber"
        value={
          editingSecrets
            ? bankAccountSecrets.accountNumber
            : secretsLoaded
              ? bankAccountSecrets.accountNumber
              : `****${bankAccount.accountNumberLast4}`
        }
        onChange={handleSecretsChange}
        disabled={!editingSecrets}
        rightSideIconStyle="mt-4 mr-4 flex gap-2"
        rightSideIcon={!viewOnly && (
          <>
            {!secretsLoaded && (
              <div className="cursor-pointer" onClick={async () => {
                await getBankAccountSecrets()
              }}>
                <LuEye size={18} className="text-text-secondary" />
              </div>
            )}
            {secretsLoaded && !editingSecrets && (
              <div className="cursor-pointer" onClick={() => {
                setEditingSecrets(true)
                setLocalBankAccountSecrets(prev => ({ ...prev, accountNumber: '' }))
                setBankAccountSecrets?.({ routingNumber: '', accountNumber: '' })
              }}>
                <LuPencil size={18} className="text-text-secondary" />
              </div>
            )}
          </>
        )}
      />
      <Input
        width="w-full"
        placeholder="Name on Account"
        label="Name on Account"
        name="nameOnAccount"
        value={bankAccount.nameOnAccount}
        onChange={handleChange}
        disabled={viewOnly}
      />
      <Input
        width="w-full"
        placeholder="Routing Number"
        label="Routing Number"
        name="routingNumber"
        value={
          editingSecrets
            ? bankAccountSecrets.routingNumber
            : secretsLoaded
              ? bankAccountSecrets.routingNumber
              : '*********'
        }
        onChange={handleSecretsChange}
        disabled={!editingSecrets}
        rightSideIconStyle="mt-4 mr-4"
        rightSideIcon={!viewOnly && (
          !secretsLoaded ? (
            <div className="cursor-pointer" onClick={async () => {
              await getBankAccountSecrets()
            }}>
              <LuEye size={18} className="text-text-secondary" />
            </div>
          ) : !editingSecrets ? (
            <div className="cursor-pointer" onClick={() => {
              setEditingSecrets(true)
              setLocalBankAccountSecrets(prev => ({ ...prev, routingNumber: '' }))
              setBankAccountSecrets?.({ routingNumber: '', accountNumber: '' })
            }}>
              <LuPencil size={18} className="text-text-secondary" />
            </div>
          ) : null
        )}
      />
    </div>
  )
}

export default BankAccountContainer